import React, { useMemo } from 'react';
import { Property } from '../types/property.types';
import { Match } from '../types/match.types';
import { fitsPairBudget } from '../utils/budget';
import { Modal, ModalBody } from './ui/Modal';
import { PropertyCard } from './ui/PropertyCard';
import { Home } from 'lucide-react';

interface BrowseTogetherModalProps {
  isOpen: boolean;
  onClose: () => void;
  properties: Property[];
  match: Match;
  onPropertyClick: (property: Property) => void;
  loading?: boolean;
}

export const BrowseTogetherModal: React.FC<BrowseTogetherModalProps> = ({
  isOpen,
  onClose,
  properties,
  match,
  onPropertyClick,
  loading = false,
}) => {
  // Keep properties whose per-person split fits the pair's shared budget.
  // Uses the same helper as usePropertyRecommendations so the two filters
  // can't drift apart.
  const filteredProperties = useMemo(
    () => properties.filter((property) =>
      fitsPairBudget(property.monthlyRent, match.budgetOverlap)
    ),
    [properties, match.budgetOverlap]
  );

  const budgetRange = match.budgetOverlap
    ? `R${Math.floor(match.budgetOverlap.min / 1000)}k - R${Math.floor(match.budgetOverlap.max / 1000)}k`
    : 'All budgets';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Browse Together"
      subtitle={`Places you can split — within each of your budgets (${budgetRange}/person)`}
      size="full"
    >
      <ModalBody>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Home className="w-12 h-12 text-primary-300 mx-auto mb-4 animate-pulse" />
              <p className="text-text-secondary">Loading properties...</p>
            </div>
          </div>
        ) : filteredProperties.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-20">
            {filteredProperties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                variant="small"
                showPerPersonSplit
                matchScore={(property as any).matchScore}
                onClick={() => {
                  onPropertyClick(property);
                  onClose();
                }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <Home className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="font-bold text-lg text-text-primary mb-2">
              No properties found
            </h3>
            <p className="text-sm text-text-secondary max-w-md">
              We couldn't find any properties that match both of your budget requirements. 
              Try adjusting your budget ranges or check back later for new listings.
            </p>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};
